import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { AssistantRuntimeProvider, useLocalRuntime } from '@assistant-ui/react';
import sendMessage, {
	fetchThreadState,
	mapThreadStateMessages,
	submitFeedback,
} from './chatApi';
import type { AgentUIConfig, Source } from './types';
import type { Suggestion } from '../../common/js/types';
import type { HydratedThreadMessage } from './chatApi';

/**
 * Filters sources to only those whose post_id is cited via [>post_id<] in the text,
 * returned in order of first appearance.
 * The RAG deduplicates retrieved context, but when the LLM calls the RAG multiple times
 * during a single response it can return duplicate sources. The `seen` Set handles that deduplication.
 */
function filterCitedSources( content: string, sources?: Source[] ): Source[] {
	if ( ! sources?.length || ! content ) {
		return [];
	}

	const sourceMap = new Map( sources.map( ( s ) => [ s.post_id, s ] ) );
	const cited: Source[] = [];
	const seen = new Set< number >();
	const markerRegex = /\[>(\d+)<\]/g;
	let match;

	while ( ( match = markerRegex.exec( content ) ) !== null ) {
		const id = Number( match[ 1 ] );
		if ( sourceMap.has( id ) && ! seen.has( id ) ) {
			seen.add( id );
			cited.push( sourceMap.get( id )! );
		}
	}

	return cited;
}

function isRootPathSource( sourceUrl: string ): boolean {
	if ( ! sourceUrl ) {
		return false;
	}

	try {
		const parsedUrl = new URL( sourceUrl, window.location.origin );
		return parsedUrl.pathname === '/';
	} catch {
		return false;
	}
}

function applyStudentSourceTitleOverrides( sources: Source[] ): Source[] {

	return sources.map( ( source ) => {
		if ( isRootPathSource( source.url ) ) {
			return {
				...source,
				title: 'Front Page',
			};
		}

		if ( ! source.title && source.snippet ) {
			const truncated = source.snippet.slice( 0, 100 );
			return {
				...source,
				title: source.snippet.length > 100
					? truncated + '…'
					: truncated,
			};
		}

		return source;
	} );
}

// Regex to matches URL optionally followed by a space and a title in quotes
const markdown_url_regex = /\]\(([^)\s]+)(?:\s+"[^"]*")?\)|\[>(\d+)<\]/g;

function buildSourceUrlMap( sources: Source[] ): Map< number, string > {
    const map = new Map< number, string >();
    for ( const source of sources ) {
        map.set( source.post_id, source.url.replace( /\/+$/, '' ).toLowerCase() );
    }
    return map;
}

function applyRedundantCitationStrip(
    text: string,
    sourceUrlMap: Map< number, string >,
    initialLastUrl: string,
): { result: string; lastLinkedUrl: string } {
    let lastLinkedUrl = initialLastUrl;

    const result = text.replace( markdown_url_regex, ( match, linkUrl, markerId ) => {
        if ( linkUrl !== undefined ) {
            lastLinkedUrl = linkUrl.replace( /\/+$/, '' ).toLowerCase();
            return match;
        }
        if ( markerId !== undefined ) {
            const sourceUrl = sourceUrlMap.get( Number( markerId ) );
            if ( sourceUrl && sourceUrl === lastLinkedUrl ) {
                return '';
            }
        }
        return match;
    } );

    return { result, lastLinkedUrl };
}

/**
 * Processes an incoming chunk of a streaming AI response to clean up redundant citations.
 *
 * WHY WE NEED IT:
 * RAG-based LLMs often generate redundant citations, placing a marker like `[>123<]` immediately
 * after a hyperlinked title `[Link](https://example.com)` that points to the exact same source.
 * We want to strip these redundant inline citation markers.
 *
 * WHAT IT DOES:
 * 1. Chunk Splitting: It looks for incomplete markdown constructs at the very tail end of the string
 * and isolates them as `pending` text.
 * 2. Deduplication: It takes the remaining `safe` text (where all markdown is complete) and uses a regex
 * to strip `[>post_id<]` markers if their resolved URL perfectly matches the most recently parsed markdown link.
 */
function stripRedundantCitationFromStream(
    text: string,
    sources: Source[],
    lastLinkedUrl: string,
): { cleaned: string; pending: string; lastLinkedUrl: string } {
    if ( ! text ) {
        return { cleaned: '', pending: '', lastLinkedUrl };
    }

    let safeText = text;
    let pending = '';

    // Only match actual unclosed markdown elements at the very end of the string
    const incompleteMatch = text.match(/(\[[^\]]*\]\([^)]*|\[[^\]]*|\[>[^<]*)$/);
    if ( incompleteMatch ) {
        const possibleTail = incompleteMatch[ 1 ];

        // Verify it is an incomplete construct
        if (
            /^\[[^\]]*\]\([^)]*$/.test( possibleTail ) ||
            /^\[[^\]]*$/.test( possibleTail ) ||
            /^\[>[^<]*$/.test( possibleTail )
        ) {
            safeText = text.slice( 0, text.length - possibleTail.length );
            pending = possibleTail;
        }
    }

    const sourceUrlMap = buildSourceUrlMap( sources );
    const { result: cleaned, lastLinkedUrl: currentLastUrl } = applyRedundantCitationStrip( safeText, sourceUrlMap, lastLinkedUrl );

    return { cleaned, pending, lastLinkedUrl: currentLastUrl };
}

/**
 * Cleans up redundant citation markers from fully generated, static messages.
 *
 * WHY WE NEED IT:
 * Exactly like `stripRedundantCitationFromStream`, this removes `[>post_id<]` markers when they immediately follow
 * a markdown link pointing to the same URL. Because this runs on completely finished strings rather
 * than unpredictable live network streams, it does not need the complex chunk-splitting or look-ahead logic.
 *
 * WHAT IT DOES:
 * Executes a single-pass regex over the complete text string, tracking the last seen markdown URL
 * and stripping any subsequent `[>post_id<]` markers that match it.
 */
function stripRedundantCitationFromText( text: string, sources?: Source[] ): string {
    if ( ! sources?.length || ! text ) {
        return text;
    }

    const sourceUrlMap = buildSourceUrlMap( sources );

    return applyRedundantCitationStrip( text, sourceUrlMap, '' ).result;
}

type TextMessagePart = {
	type: 'text';
	text: string;
};

type ContentPart = TextMessagePart;

type RuntimeYield = {
	content: ContentPart[];
	metadata?: {
		custom: {
			messageId?: string;
			sources?: Source[];
			suggestion?: Suggestion;
		};
	};
};

type PromptProvenance = {
	promptSource?: 'suggested' | 'organic';
	promptKey?: string;
};

type RunArguments = {
	messages: {
		content?: { type: string; text: string }[];
		metadata?: { custom?: PromptProvenance };
	}[];
	abortSignal?: AbortSignal;
};

interface NewChatContextValue {
	startNewChat: () => void;
	clearHistory: () => void;
	stopResponse: () => void;
	dismissStoppedState: () => void;
	resumeThread: (
		threadId: string,
		initialMessages?: HydratedThreadMessage[]
	) => Promise<void>;
	hasMessages: boolean;
	messagesSentCount: number;
	activeThreadId: string;
	isResponding: boolean;
	isResumedThread: boolean;
	isRestoringThread: boolean;
	lastUserPrompt: string;
	wasStopped: boolean;
	threadId: string;
	lastError: unknown | null;
	dismissError: () => void;
	retryLastMessage: () => void;
}

export const NewChatContext = createContext<NewChatContextValue | null>( null );

export function useNewChat() {
	const context = useContext( NewChatContext );
	if ( ! context ) {
		throw new Error( 'useNewChat must be used within RuntimeProvider' );
	}
	return context;
}

const THREAD_STORAGE_KEY = 'uc-agent-thread-id';

export default function RuntimeProvider( {
	children,
	config = {},
}: Readonly<{
	children: React.ReactNode;
	config?: AgentUIConfig;
}> ) {

	// Restore threadId from sessionStorage if available, otherwise generate a new one
	const [ threadId, setThreadId ] = useState( () => {
		const saved = sessionStorage.getItem( THREAD_STORAGE_KEY );
		return saved || crypto.randomUUID();
	} );
	const [ hasMessages, setHasMessages ] = useState( false );
	const [ isResumedThread, setIsResumedThread ] = useState( false );
	const [ messagesSentCount, setMessagesSentCount ] = useState( 0 );
	const [ isResponding, setIsResponding ] = useState( false );
	const [ isRestoringThread, setIsRestoringThread ] = useState( () => {
		const saved = sessionStorage.getItem( THREAD_STORAGE_KEY );
		return Boolean( saved && config.agentHost && config.siteId );
	} );
	const [ lastUserPrompt, setLastUserPrompt ] = useState( '' );
	const [ lastUserPromptProvenance, setLastUserPromptProvenance ] = useState<PromptProvenance>( {} );
	const [ wasStopped, setWasStopped ] = useState( false );
	const [ lastError, setLastError ] = useState<unknown | null>( null );
	const activeRequestControllerRef = useRef<AbortController | null>( null );
	// Per-request stop/new-chat flags, keyed by that request's own AbortController.
	// Keeping these off a shared ref means an old, still-cleaning-up stream can't
	// stomp on a newer stream's state (or vice versa) when requests overlap.
	const requestStateRef = useRef(
		new WeakMap<AbortController, { stopRequested: boolean; newChatRequested: boolean }>()
	);

	// Persist threadId to sessionStorage whenever it changes
	useEffect( () => {
		sessionStorage.setItem( THREAD_STORAGE_KEY, threadId );
	}, [ threadId ] );

	const chatModelAdapter = useMemo(
		() => ( {
			async* run( { messages, abortSignal }: RunArguments ): AsyncGenerator<RuntimeYield, void, void> {
				setHasMessages( messages.length > 0 );
				setWasStopped( false );
				setLastError( null );
				setIsResponding( true );
				const requestController = new AbortController();
				const requestState = { stopRequested: false, newChatRequested: false };
				requestStateRef.current.set( requestController, requestState );
				activeRequestControllerRef.current = requestController;
				const combinedAbortSignal = AbortSignal.any( [ abortSignal, requestController.signal ] );

				let cleanedResponse = '';
				let pendingTail = '';
				let messageId: string | undefined;
				let suggestion: Suggestion | undefined;
				const sources: Source[] = [];

				try {
					// Get the last user message
					const lastMessage = messages[ messages.length - 1 ];
					const userMessage = lastMessage?.content?.find( ( contentPart ) => contentPart.type === 'text' )?.text ?? '';
					const promptProvenance: PromptProvenance = {
						promptSource: lastMessage?.metadata?.custom?.promptSource,
						promptKey: lastMessage?.metadata?.custom?.promptKey,
					};
					setLastUserPrompt( userMessage );
					setLastUserPromptProvenance( promptProvenance );

					// Show a small in-bubble loading indicator immediately.
					yield {
						content: [
							{
								type: 'text',
								text: '…',
							},
						],
					};

					// Stream response from your /ask endpoint
					const stream = await sendMessage( {
						agentHost: config.agentHost,
						threadId,
						messages: [ {
							role: 'user',
							content: userMessage,
							...( promptProvenance.promptSource ? { data: promptProvenance } : {} ),
						} ],
						abortSignal: combinedAbortSignal,
					} );

					let fullResponse = '';
					let lastLinkedUrl = '';
					const seenPostIds = new Set< number >();
					let sourcesReceived = false;
					let statusText = '';

					for await ( const chunk of stream ) {

						if ( chunk.type === 'text' ) {
							fullResponse += chunk.data;

							if ( ! sourcesReceived ) {
								// Pre-response text from the LLM; stay in loading state.
								continue;
							}

							// Process only new text (pending tail from last chunk + new data).
							const result = stripRedundantCitationFromStream(
								pendingTail + chunk.data,
								sources,
								lastLinkedUrl,
							);
							cleanedResponse += result.cleaned;
							pendingTail = result.pending;
							lastLinkedUrl = result.lastLinkedUrl;
						} else if ( chunk.type === 'status' ) {
							statusText = chunk.data;
							yield {
								content: [ { type: 'text', text: '…' } ],
								metadata: { custom: { statusText } },
							};
							continue;
						} else if ( chunk.type === 'sources' ) {
							const isFirstSourcesChunk = ! sourcesReceived;
							sourcesReceived = true;
							statusText = '';
							const normalizedSources = applyStudentSourceTitleOverrides( chunk.data );
							for ( const s of normalizedSources ) {
								if ( ! seenPostIds.has( s.post_id ) ) {
									seenPostIds.add( s.post_id );
									sources.push( s );
								}
							}
							if ( isFirstSourcesChunk ) {
								// Discard any pre-response text — the real answer follows sources.
								fullResponse = '';
								cleanedResponse = '';
								pendingTail = '';
								lastLinkedUrl = '';
							}
							continue;
						} else if ( chunk.type === 'messageId' ) {
							messageId = chunk.data;
							continue;
						} else if ( chunk.type === 'suggestion' ) {
							// Held for the final yield, so the panel never shows mid-stream.
							suggestion = chunk.data;
							continue;
						} else {
							continue;
						}

						yield {
							content: [ { type: 'text', text: cleanedResponse } ],
							metadata: {
								custom: {
									messageId,
									sources: filterCitedSources( cleanedResponse + pendingTail, sources ),
									isStreaming: true,
								},
							},
						};
					}

					// No sources received — this was a conversational reply. Use accumulated text as-is.
					if ( ! sourcesReceived && fullResponse ) {
						const result = stripRedundantCitationFromStream( pendingTail + fullResponse, sources, lastLinkedUrl );
						cleanedResponse = result.cleaned + result.pending;
					}

					// Final yield: flush any remaining pending text.
					if ( fullResponse ) {
						if ( pendingTail ) {
							const finalResult = stripRedundantCitationFromStream( pendingTail, sources, lastLinkedUrl );
							cleanedResponse += finalResult.cleaned;
							// Any still-pending tail is malformed markdown; include as-is.
							cleanedResponse += finalResult.pending;
						}

						const usedSources = filterCitedSources( cleanedResponse, sources );
						yield {
							content: [ { type: 'text', text: cleanedResponse } ],
							metadata: {
								custom: { messageId, sources: usedSources, suggestion },
							},
						};
					}

					// ai_search_performed fires only when the LLM retrieved sources (a real search),
					// not for conversational replies like greetings or follow-ups with no RAG results.
					if ( sources.length > 0 ) {
						window.uconnect?.analytics?.send_event?.( 'ai_search_performed', { thread_id: threadId } );
					}
					setMessagesSentCount( ( c ) => c + 1 );
				} catch ( error ) {
					if ( requestState.stopRequested ) {
						if ( sources.length > 0 ) {
							window.uconnect?.analytics?.send_event?.( 'ai_search_performed', { thread_id: threadId } );
						}
						// Skip the yield when startNewChat() was the caller — the thread has
						// already been replaced and yielding into it would crash the runtime.
						if ( ! requestState.newChatRequested ) {
							yield {
								content: [ { type: 'text', text: cleanedResponse || '…' } ],
								metadata: {
									custom: {
										messageId,
										sources: filterCitedSources( cleanedResponse + pendingTail, sources ),
										wasStopped: true,
									},
								},
							};
							setMessagesSentCount( ( c ) => c + 1 );
						}
						return;
					}
					setLastError( error );
					yield {
						content: [ { type: 'text', text: '' } ],
					};
				} finally {
					requestStateRef.current.delete( requestController );
					if ( activeRequestControllerRef.current === requestController ) {
						setIsResponding( false );
						if ( requestState.stopRequested && ! requestState.newChatRequested ) {
							setWasStopped( true );
						}
						activeRequestControllerRef.current = null;
					}
				}
			},
		} ),
		[ config.agentHost, threadId ]
	);

	const stopResponse = useCallback( () => {
		const controller = activeRequestControllerRef.current;
		if ( ! controller ) {
			return;
		}
		const state = requestStateRef.current.get( controller );
		if ( state ) {
			state.stopRequested = true;
		}
		controller.abort();
	}, [] );

	const dismissStoppedState = useCallback( () => {
		setWasStopped( false );
	}, [] );

	const dismissError = useCallback( () => {
		setLastError( null );
	}, [] );

	const feedbackAdapter = useMemo(
		() => ( {
			submit: async ( { message, type }: { message: any; type: 'positive' | 'negative' } ) => {
				try {
					const messageId = message.metadata?.custom?.messageId
						|| message.id;
					const value = type === 'positive' ? 'upvote' : 'downvote';

					if ( ! messageId || ! config.agentHost ) {
						return;
					}

					await submitFeedback( {
						agentHost: config.agentHost,
						threadId,
						messageId,
						value,
					} );
				} catch ( error ) {
					console.error( 'Failed to submit feedback:', error );
				}
			},
		} ),
		[ config.agentHost, threadId ]
	);

	const runtime = useLocalRuntime( chatModelAdapter, {
		adapters: {
			feedback: feedbackAdapter,
		},
	} );

	const retryLastMessage = useCallback( () => {
		if ( ! runtime || isResponding || ! lastUserPrompt ) {
			return;
		}

		setLastError( null );
		runtime.thread.append( {
			role: 'user',
			content: [ { type: 'text', text: lastUserPrompt } ],
			...( lastUserPromptProvenance.promptSource
				? { metadata: { custom: lastUserPromptProvenance } }
				: {} ),
		} );
	}, [ isResponding, lastUserPrompt, lastUserPromptProvenance, runtime ] );

	const resumeThread = useCallback( async (
		nextThreadId: string,
		initialMessages?: HydratedThreadMessage[]
	) => {
		if ( ! nextThreadId ) {
			return;
		}
		const hydratedMessages = ( initialMessages ?? [] ).map( ( message ) => {
			const normalizedSources = applyStudentSourceTitleOverrides( message.sources ?? [] );
			const cleanedContent = stripRedundantCitationFromText( message.content, message.sources );
			const usedSources = filterCitedSources( cleanedContent, normalizedSources );

			return {
				role: message.role,
				id: message.id,
				content: [
					{
						type: 'text' as const,
						text: cleanedContent,
					},
				],
				metadata: {
					custom: {
						messageId: message.id,
						sources: usedSources,
						feedback: message.feedback,
					},
				},
			};
		} );

		// Ensure thread switch completes before hydrating messages to avoid race conditions.
		await runtime.threads.switchToNewThread();
		runtime.thread.reset( hydratedMessages );
		setIsResumedThread( hydratedMessages.length > 0 );
		setThreadId( nextThreadId );
		setHasMessages( hydratedMessages.length > 0 );
	}, [ runtime ] );

	// Restore thread messages from the API on mount if a saved threadId exists
	const hasRestoredThread = useRef( false );
	useEffect( () => {
		if ( hasRestoredThread.current ) {
			return;
		}
		const savedThreadId = sessionStorage.getItem( THREAD_STORAGE_KEY );
		if ( ! savedThreadId || ! config.agentHost || ! config.siteId ) {
			return;
		}
		hasRestoredThread.current = true;

		( async () => {
			try {
				const threadState = await fetchThreadState( {
					agentHost: config.agentHost,
					siteId: config.siteId,
					threadId: savedThreadId,
				} );
				const messages = mapThreadStateMessages( threadState.messages ?? [] );
				if ( messages.length > 0 ) {
					await resumeThread( savedThreadId, messages );
				}
			} catch {
				// Silently continue with empty thread if restoration fails
			} finally {
				setIsRestoringThread( false );
			}
		} )();
	}, [ config.agentHost, config.siteId, resumeThread ] );

	const startNewChat = useCallback( () => {
		const controller = activeRequestControllerRef.current;
		if ( controller ) {
			const state = requestStateRef.current.get( controller );
			if ( state ) {
				state.newChatRequested = true;
			}
			stopResponse();
		}
		setIsResumedThread( false );
		setThreadId( crypto.randomUUID() );
		setHasMessages( false );
		setWasStopped( false );
		runtime.threads.switchToNewThread();
	}, [ runtime, stopResponse ] );

	const clearHistory = useCallback( () => {
		startNewChat();
	}, [ startNewChat ] );

	const newChatContextValue = useMemo(
		() => ( {
			startNewChat,
			clearHistory,
			stopResponse,
			dismissStoppedState,
			dismissError,
			resumeThread,
			hasMessages,
			messagesSentCount,
			activeThreadId: threadId,
			isResponding,
			isResumedThread,
			isRestoringThread,
			lastUserPrompt,
			wasStopped,
			threadId,
			lastError,
			retryLastMessage,
		} ),
		[
			clearHistory,
			dismissError,
			dismissStoppedState,
			hasMessages,
			isResumedThread,
			lastError,
			messagesSentCount,
			resumeThread,
			retryLastMessage,
			startNewChat,
			stopResponse,
			threadId,
			isResponding,
			isRestoringThread,
			lastUserPrompt,
			wasStopped,
		]
	);

	return (
		<AssistantRuntimeProvider runtime={ runtime }>
			<NewChatContext.Provider value={ newChatContextValue }>
				{ children }
			</NewChatContext.Provider>
		</AssistantRuntimeProvider>
	);
}
